import { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Target, Zap, History, Activity, FileUp } from 'lucide-react';
import ProspectCriteriaForm from './ProspectCriteriaForm';
import ConnectorSelector from './ConnectorSelector';
import RunStatusPoller from './RunStatusPoller';
import BatchListPage from './BatchListPage';
import CsvSeedUpload from './CsvSeedUpload';
import { useRoleInfo } from '@/hooks/usePermissions';
import { useToast } from '@/components/common/Toast';
import { PageHeader } from '@/components/common/PageHeader';
import type { ProspectingCriteria, ConnectorSelection } from '../services/prospectingService';
import { startProspectingRun, getBatches } from '../services/prospectingService';

const EMPTY_CONNECTORS: ConnectorSelection = {
  discovery:  '',
  enrichment: '',
  email:      '',
  llm:        '',
  linkedin:   '',
  webhook:    'none',
};

export default function ProspectingPage() {
  const navigate = useNavigate();
  const [running, setRunning]             = useState(false);
  const [activeBatchId, setActiveBatchId] = useState<string | null>(null);
  const [connectors, setConnectors]       = useState<ConnectorSelection>(EMPTY_CONNECTORS);
  const [batches, setBatches]             = useState<any[]>([]);
  const [batchesLoaded, setBatchesLoaded] = useState(false);
  const [showCsvUpload, setShowCsvUpload] = useState(false);

  // Agent Logs is admin/manager-only — matches the backend /agent-logs route (ADMIN, ABM_MANAGER)
  const { isAdmin, isABMManager } = useRoleInfo();
  const canViewLogs = isAdmin || isABMManager;

  const { addToast } = useToast();
  const showToast = (type: 'success' | 'error' | 'info', msg: string) =>
    addToast({ type, title: msg });

  const refreshBatches = useCallback(async () => {
    try {
      const res = await getBatches();
      setBatches(res.data);
      setBatchesLoaded(true);
    } catch { /* non-critical */ }
  }, []);

  useEffect(() => { refreshBatches(); }, [refreshBatches]);

  const handleStartRun = async (criteria: ProspectingCriteria) => {
    if (!connectors.discovery || !connectors.llm) {
      showToast('error', 'Select a Discovery connector and an AI/LLM connector before running.');
      return;
    }
    setRunning(true);
    try {
      const result = await startProspectingRun(criteria, connectors);
      setActiveBatchId(result.batchId);
      showToast('success', 'Pipeline started — AI agents are researching prospects.');
      refreshBatches();
    } catch (err: any) {
      showToast('error', err.response?.data?.error || err.message);
    } finally {
      setRunning(false);
    }
  };

  const handleRunComplete = useCallback((status: string) => {
    refreshBatches();
    if (status === 'REVIEW' && activeBatchId) {
      showToast('success', 'Prospects ready for review!');
      setTimeout(() => navigate(`/prospecting/batches/${activeBatchId}`), 1500);
    } else if (status === 'FAILED') {
      showToast('error', 'Pipeline failed — check Agent Logs for details.');
    }
  }, [activeBatchId, navigate, refreshBatches]);

  const readyToRun = !!(connectors.discovery && connectors.llm);

  return (
    <div className="space-y-6">
      {/* Header */}
      <PageHeader
        title="Vibe Prospecting"
        description="Configure data sources, set criteria, let AI agents hunt prospects"
        icon={Target}
        actions={
          <>
            <button
              onClick={() => setShowCsvUpload(true)}
              className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground border border-border rounded-lg px-3 py-1.5 hover:bg-muted transition-colors"
            >
              <FileUp className="h-3.5 w-3.5" />Import CSV
            </button>
            {canViewLogs && (
              <button
                onClick={() => navigate('/prospecting/agent-logs')}
                className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground border border-border rounded-lg px-3 py-1.5 hover:bg-muted transition-colors"
              >
                <Activity className="h-3.5 w-3.5" />Agent Logs
              </button>
            )}
          </>
        }
      />

      <div className="grid grid-cols-1 xl:grid-cols-5 gap-6">
        {/* Left: Config + Form */}
        <div className="xl:col-span-2 space-y-4">
          {/* Connector selector */}
          <div className="rounded-xl border border-border bg-card p-5">
            <ConnectorSelector selection={connectors} onChange={setConnectors} />
          </div>

          {/* Criteria form / status poller */}
          <div className="rounded-xl border border-border bg-card p-5">
            <div className="flex items-center gap-2 mb-4">
              <Zap className="h-4 w-4 text-primary" />
              <h2 className="text-sm font-semibold">Search Criteria</h2>
            </div>

            {!activeBatchId ? (
              <>
                {!readyToRun && (
                  <div className="mb-4 px-3 py-2.5 rounded-lg bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800 text-xs text-amber-700 dark:text-amber-400">
                    Add API keys for <strong>Discovery</strong> and <strong>AI / LLM</strong> connectors above to start a run.
                  </div>
                )}
                <ProspectCriteriaForm onSubmit={handleStartRun} loading={running} disabled={!readyToRun} />
              </>
            ) : (
              <div className="space-y-4">
                <RunStatusPoller
                  batchId={activeBatchId}
                  onComplete={(status) => {
                    handleRunComplete(status);
                    if (status !== 'RUNNING' && status !== 'PENDING') {
                      setTimeout(() => setActiveBatchId(null), 3000);
                    }
                  }}
                />
                <button
                  onClick={() => setActiveBatchId(null)}
                  className="w-full py-2 rounded-lg border border-border text-xs text-muted-foreground hover:bg-muted transition-colors"
                >
                  Start another run
                </button>
              </div>
            )}
          </div>

          {/* Pipeline info */}
          <div className="rounded-xl border border-border bg-card/50 p-4">
            <p className="text-xs font-medium text-muted-foreground mb-3">5-Stage Pipeline</p>
            {[
              { n: 1, name: 'Discovery',  slot: 'discovery',  desc: 'Find matching companies',    required: true },
              { n: 2, name: 'Enrichment', slot: 'enrichment', desc: 'Pain axis + maturity',       required: false },
              { n: 3, name: 'Strategy',   slot: 'llm',        desc: 'Win themes + use cases',     required: true },
              { n: 4, name: 'Contacts',   slot: 'email',      desc: 'Domain + key contact',       required: false },
              { n: 5, name: 'Outreach',   slot: 'llm',        desc: 'Email, call, LinkedIn copy', required: true },
            ].map(stage => {
              const connId  = connectors[stage.slot as keyof ConnectorSelection] as string;
              const isSet   = !!(connId && connId !== 'none');
              return (
                <div key={stage.n} className="flex items-center gap-2 py-1.5 border-b border-border/50 last:border-0">
                  <span className={`h-5 w-5 rounded-full text-xs font-bold flex items-center justify-center shrink-0 ${
                    isSet ? 'bg-primary/10 text-primary' : stage.required ? 'bg-amber-100 dark:bg-amber-950/20 text-amber-600' : 'bg-muted text-muted-foreground'
                  }`}>{stage.n}</span>
                  <div className="flex-1 min-w-0">
                    <span className="text-xs font-medium">{stage.name}</span>
                    {stage.required && <span className="text-xs text-red-400 ml-1">*</span>}
                    <span className="text-xs text-muted-foreground ml-1">— {stage.desc}</span>
                  </div>
                  <span className={`text-xs px-1.5 py-0.5 rounded shrink-0 ${
                    isSet ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground'
                  }`}>
                    {isSet ? connId.split('_')[0] : 'not set'}
                  </span>
                </div>
              );
            })}
            <p className="text-xs text-muted-foreground mt-2"><span className="text-red-400">*</span> Required</p>
          </div>
        </div>

        {/* Right: Batch history */}
        <div className="xl:col-span-3 space-y-4">
          <div className="flex items-center gap-2">
            <History className="h-4 w-4 text-muted-foreground" />
            <h2 className="text-sm font-semibold">Recent Runs</h2>
          </div>
          <BatchListPage
            externalBatches={batches}
            loaded={batchesLoaded}
            onRefresh={refreshBatches}
          />
        </div>
      </div>

      {/* CSV seed upload modal */}
      {showCsvUpload && (
        <CsvSeedUpload
          onImported={(batchId) => {
            setShowCsvUpload(false);
            refreshBatches();
            navigate(`/prospecting/batches/${batchId}`);
          }}
          onClose={() => setShowCsvUpload(false)}
        />
      )}
    </div>
  );
}
